/**
 * Geometry for the trip page's flight map (src/components/flight/FlightMap.jsx).
 * Routes are [lat, lon] points from flight tracking.
 */

const toRad = (d) => (d * Math.PI) / 180;

/** Keep longitudes continuous across the antimeridian, so a line to Asia or Hawaii doesn't wrap the globe. */
export function unwrapLongitudes(route) {
  const out = [];
  let shift = 0;
  route.forEach(([lat, lon], i) => {
    if (i > 0) {
      const prev = route[i - 1][1] + shift;
      const here = lon + shift;
      if (here - prev > 180) shift -= 360;
      else if (prev - here > 180) shift += 360;
    }
    out.push([lat, lon + shift]);
  });
  return out;
}

/** Initial bearing from a to b, degrees clockwise from north. */
export function bearing([lat1, lon1], [lat2, lon2]) {
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2));
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Rough distance, good enough to place a point along a route. */
function distance([lat1, lon1], [lat2, lon2]) {
  const k = Math.cos(toRad((lat1 + lat2) / 2));
  return Math.hypot(lat2 - lat1, (lon2 - lon1) * k);
}

/** The point `fraction` of the way along the route, the index it falls after, and the heading there. */
export function pointAlongRoute(route, fraction) {
  if (!Array.isArray(route) || route.length < 2) return null;
  const legs = [];
  let total = 0;
  for (let i = 1; i < route.length; i += 1) {
    const d = distance(route[i - 1], route[i]);
    legs.push(d);
    total += d;
  }
  let left = Math.max(0, Math.min(1, fraction)) * total;
  for (let i = 0; i < legs.length; i += 1) {
    if (left <= legs[i] || i === legs.length - 1) {
      const t = legs[i] ? Math.min(1, left / legs[i]) : 0;
      const [a, b] = [route[i], route[i + 1]];
      return { point: [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t], after: i, heading: bearing(a, b) };
    }
    left -= legs[i];
  }
  return { point: route[0], after: 0, heading: bearing(route[0], route[1]) };
}

/** The route point nearest a position, so the flown part ends at the plane. */
export function nearestIndex(route, [lat, lon]) {
  let best = 0;
  let bestD = Infinity;
  route.forEach((p, i) => {
    const d = distance(p, [lat, lon]);
    if (d < bestD) { bestD = d; best = i; }
  });
  return best;
}

/** Share of the flying time since takeoff, 0 before it leaves the runway. */
export function timeFraction(status, nowMs) {
  const off = Date.parse(status?.details?.actual_off || '');
  const on = Date.parse(status?.estimated_in || status?.scheduled_in || '');
  if (!Number.isFinite(off) || !Number.isFinite(on) || on <= off) return 0;
  return Math.max(0, Math.min(1, (nowMs - off) / (on - off)));
}

/**
 * Where to draw the plane: its reported position when there is one,
 * otherwise where the clock says it should be along the route.
 */
export function planeOnRoute(route, position, status, nowMs) {
  if (!Array.isArray(route) || route.length < 2) return null;
  if (Number.isFinite(position?.latitude) && Number.isFinite(position?.longitude)) {
    // Bring the reported longitude onto the same side as the unwrapped route.
    let lon = position.longitude;
    const ref = route[nearestIndex(route, [position.latitude, lon])][1];
    while (lon - ref > 180) lon -= 360;
    while (ref - lon > 180) lon += 360;
    const after = nearestIndex(route, [position.latitude, lon]);
    const next = route[Math.min(after + 1, route.length - 1)];
    return {
      point: [position.latitude, lon],
      after,
      heading: Number.isFinite(position.heading) ? position.heading : bearing(route[after], next),
    };
  }
  return pointAlongRoute(route, timeFraction(status, nowMs));
}
